import { useState, useEffect, useRef, useCallback } from "react";
import "./homepage.css";
import Footer from '../Component/NewFooter';
import axios from "axios";
import SplashScreen from "../Component/SplashScreen";

import {
  FaWrench, FaBoxOpen, FaHardHat,
  FaClipboardCheck, FaSatelliteDish, FaShip,
} from "react-icons/fa";

const BASE_URL      = "https://api.jeyshawnng.com";
const FETCH_TIMEOUT = 8000;
// Bumped to v2: older cached payloads (pre "welcome" nesting) don't match
// the current shape and were crashing the page on load. Bumping the key
// forces a clean re-fetch for anyone holding a stale v1 cache.
const CACHE_KEY      = "jeyshawn_homepage_data_v2";

const serviceIcons = {
  Engineering:                        <FaWrench />,
  Procurement:                        <FaBoxOpen />,
  Construction:                       <FaHardHat />,
  Commissioning:                      <FaClipboardCheck />,
  "Installation and Instrumentation": <FaSatelliteDish />,
  "Marine Support":                   <FaShip />,
};

/* ── Helpers ───────────────────────────────────────────────────── */
function parseApiData(db) {
  return {
    announcement: db?.announcement || "",
    welcome: {
      heading: db?.about?.heading || "",
      paragraphs: [
        db?.about?.paragraph1,
        db?.about?.paragraph2,
        db?.about?.paragraph3,
      ].filter(Boolean),
      stats: {
        manHours: db?.about?.statHours    || "",
        projects: db?.about?.statProjects || "",
      },
      videoUrl:  db?.about?.videoUrl || "",
      avatarImg: db?.about?.image    || "",
    },
    heroSlides: Array.isArray(db?.heroSlides)
      ? db.heroSlides.filter((s) => s.bg)
      : [],
    services:   Array.isArray(db?.services)          ? db.services         : [],
    coreValues: Array.isArray(db?.coreValues?.items) ? db.coreValues.items : [],
    clients: {
      subtitle: db?.clientsSection?.subtitle || "",
      logos: Array.isArray(db?.clients)
        ? db.clients.filter((c) => c.logo).map((c) => ({ name: c.name, logo: c.logo }))
        : [],
    },
  };
}

// Guards against stale/malformed localStorage payloads (e.g. cached under
// an old shape before a schema change) crashing the render before the
// background fetch can replace it with fresh, correctly-shaped data.
function isValidPageData(obj) {
  return (
    obj &&
    typeof obj === "object" &&
    obj.welcome &&
    typeof obj.welcome === "object" &&
    Array.isArray(obj.heroSlides) &&
    Array.isArray(obj.services) &&
    Array.isArray(obj.coreValues) &&
    obj.clients &&
    Array.isArray(obj.clients.logos)
  );
}

function readCache() {
  try {
    const parsed = JSON.parse(localStorage.getItem(CACHE_KEY));
    if (!isValidPageData(parsed)) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}
function writeCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
}

/* ── Sub-components ────────────────────────────────────────────── */
function ErrorScreen({ onRetry }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "#09090b", zIndex: 9999,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px",
    }}>
      <div style={{ textAlign: "center" }}>
        <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "1.1rem", fontFamily: "Georgia, serif", letterSpacing: "0.04em", marginBottom: "8px" }}>
          Poor internet connection.
        </p>
        <p style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.8rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>
          Please check your connection and try again.
        </p>
      </div>
      <button
        onClick={onRetry}
        style={{
          marginTop: "8px", padding: "10px 32px", background: "transparent",
          border: "1px solid rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.7)",
          fontSize: "0.8rem", letterSpacing: "0.12em", textTransform: "uppercase",
          cursor: "pointer", fontFamily: "Georgia, serif", transition: "border-color 0.2s, color 0.2s",
        }}
        onMouseEnter={e => { e.target.style.borderColor = "#d4af72"; e.target.style.color = "#d4af72"; }}
        onMouseLeave={e => { e.target.style.borderColor = "rgba(255,255,255,0.25)"; e.target.style.color = "rgba(255,255,255,0.7)"; }}
      >
        Try Again
      </button>
    </div>
  );
}

function ServiceCard({ icon, title, desc, href }) {
  return (
    <div className="service-card">
      <div className="service-card__icon">{icon}</div>
      <h3>{title}</h3>
      <p>{desc}</p>
      <a href={href} className="btn btn--navy">Learn More</a>
    </div>
  );
}

/* ── Page ──────────────────────────────────────────────────────── */
const Homepage = () => {
  const cached = readCache();

  const [status,    setStatus]    = useState(cached ? "ready" : "splash-loading");
  const [pageData,  setPageData]  = useState(cached || null);
  const [current,       setCurrent]       = useState(0);
  const [videoOpen,     setVideoOpen]     = useState(false);
  const [currentIndex,  setCurrentIndex]  = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const intervalRef  = useRef(null);
  const itemsPerView = 6;

  /* ── fetch ── */
  const doFetch = useCallback(async (background = false) => {
    const controller = new AbortController();
    const timeoutId  = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

    try {
      const { data } = await axios.get(`${BASE_URL}/api/homepage`, { signal: controller.signal });
      clearTimeout(timeoutId);

      const parsed = parseApiData(data?.data);
      writeCache(parsed);
      setPageData(parsed);

      if (!background) setStatus("splash-done");
    } catch {
      clearTimeout(timeoutId);
      if (!background) setStatus("error");
    }
  }, []);

  useEffect(() => {
    if (cached) {
      doFetch(true);
    } else {
      doFetch(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── hero auto-slide ── */
  const slides = pageData?.heroSlides || [];

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setCurrent((c) => (c + 1) % slides.length), 8500);
    return () => clearInterval(t);
  }, [slides.length]);

  const go = (n) => setCurrent((n + slides.length) % slides.length);

  /* ── clients carousel ── */
  const clients   = pageData?.clients?.logos || [];
  const totalSets = Math.ceil(clients.length / itemsPerView) || 1;
  const maxIndex  = Math.max(totalSets - 1, 0);

  const nextSlide = useCallback(() => setCurrentIndex((p) => (p >= maxIndex ? 0 : p + 1)), [maxIndex]);
  const prevSlide = useCallback(() => setCurrentIndex((p) => (p <= 0 ? maxIndex : p - 1)), [maxIndex]);

  useEffect(() => {
    if (isAutoPlaying) intervalRef.current = setInterval(nextSlide, 4000);
    return () => clearInterval(intervalRef.current);
  }, [isAutoPlaying, nextSlide]);

  /* ── Render gates ── */
  if (status === "error") return <ErrorScreen onRetry={() => { setStatus("splash-loading"); doFetch(false); }} />;
  if (status === "splash-loading") return <SplashScreen blocking />;
  // Defense in depth: if we somehow reach here without usable pageData
  // (e.g. a race on a very fast re-render), fall back to the splash
  // screen instead of crashing on data.welcome.heading.
  if (!pageData) return <SplashScreen blocking />;

  const data        = pageData;
  const activeSlide = slides[current] || {};

  return (
    <>
      {status === "splash-done" && (
        <SplashScreen autoExit onDone={() => setStatus("ready")} />
      )}

      {/* ── Hero ── */}
      <section className="hero" id="home">
        <div className="hero__bg" style={{ backgroundImage: `url(${activeSlide.bg})` }} />
        <div className="hero__content">
          <h1>{activeSlide.title}</h1>
          <a href="#about" className="btn btn--orange">Read More</a>
        </div>
        <button className="hero__arrow hero__arrow--prev" onClick={() => go(current - 1)} aria-label="Previous slide">&#8249;</button>
        <button className="hero__arrow hero__arrow--next" onClick={() => go(current + 1)} aria-label="Next slide">&#8250;</button>
        <div className="hero__dots">
          {slides.map((_, i) => (
            <button key={i} onClick={() => go(i)} className={`hero__dot${i === current ? " hero__dot--active" : ""}`} aria-label={`Go to slide ${i + 1}`} />
          ))}
        </div>
      </section>

      {/* ── Announcement ── */}
      <div className="announcement">
        <div className="announcement_carrier">
          <p>{data.announcement}</p>
          <a href="#about" className="btn btn--dark">Read More</a>
        </div>
      </div>

      {/* ── Welcome / About ── */}
      <section
        className="section light_green" id="about"
        style={{ backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="welcome__grid">
          <div className="welcome__text">
            <h2>{data.welcome?.heading}</h2>
            {(data.welcome?.paragraphs || []).map((p, i) => <p key={i}>{p}</p>)}
            <section className="stats-wrap">
              <div className="stat-block">
                <div className="stat-number">{data.welcome?.stats?.manHours}</div>
                <div className="stat-label">Man Hours With Zero Fatality</div>
              </div>
              <div className="divider" aria-hidden="true" />
              <div className="stat-block">
                <div className="stat-number">{data.welcome?.stats?.projects} <span className="accent">PROJECTS</span></div>
                <div className="stat-label">With 100% Completion Rate</div>
              </div>
            </section>
          </div>

          <div className="welcome__images">
            <img src={data.welcome?.avatarImg} alt="Oilfield worker" />
            <button className="play-btn" onClick={() => setVideoOpen(true)}>
              <span className="play-btn__icon">
                <svg viewBox="0 0 10 12" xmlns="http://www.w3.org/2000/svg"><path d="M0 0 L10 6 L0 12 Z" /></svg>
              </span>
            </button>
          </div>

          {videoOpen && (
            <div className="video-modal-overlay" onClick={(e) => e.target === e.currentTarget && setVideoOpen(false)}>
              <div className="video-modal">
                <button className="video-modal__close" onClick={() => setVideoOpen(false)}>✕</button>
                <iframe
                  src={data.welcome?.videoUrl}
                  allow="autoplay; encrypted-media; fullscreen"
                  allowFullScreen title="Company Video"
                />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── Services ── */}
      <section className="section section--light" id="services">
        <h2 className="section__title">What We Do</h2>
        <div className="services__grid">
          {(data.services || []).map((s, i) => (
            <ServiceCard key={i} icon={serviceIcons[s.title] || <FaWrench />} title={s.title} desc={s.desc} href={s.href} />
          ))}
        </div>
      </section>

      {/* ── Core Values ── */}
      <section className="section section--navy" id="values">
        <div className="cv-section">
          <div className="cv-label">Core Values</div>
          <div className="cv-grid">
            {(data.coreValues || []).map((v, i) => (
              <div className="cv-item" key={i}>
                <div className="cv-name">{v.name}</div>
                <div className="cv-desc">{v.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Clients ── */}
      <section
        className="clients-section"
        onMouseEnter={() => setIsAutoPlaying(false)}
        onMouseLeave={() => setIsAutoPlaying(true)}
      >
        <h2 className="clients__title">Our Clients</h2>
        <p className="clients__subtitle">{data.clients?.subtitle}</p>
        <div className="clients__carousel">
          <button className="clients__arrow clients__arrow--prev" onClick={prevSlide}>‹</button>
          <div className="clients__viewport">
            <div className="clients__track" style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
              {clients.map((client, i) => (
                <div key={i} className="clients__card">
                  <img src={client.logo} alt={client.name} className="clients__logo-img" />
                </div>
              ))}
            </div>
          </div>
          <button className="clients__arrow clients__arrow--next" onClick={nextSlide}>›</button>
        </div>
        <div className="clients__dots">
          {Array.from({ length: totalSets }).map((_, i) => (
            <button
              key={i}
              className={`clients__dot ${i === currentIndex ? "clients__dot--active" : ""}`}
              onClick={() => setCurrentIndex(i)}
            />
          ))}
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Homepage;